// The project's musical clock (ui-decisions D20): the beat is a pure function of time from an anchor `{ beat0, t0 }`, re-anchored on every change so the beat never jumps.
export const MIN_BPM = 20
export const MAX_BPM = 300
export const BEATS_PER_BAR = 4
export const DEFAULT_BPM = 120
export const MAX_OFFSET_MS = 2000
// A gap this long (ms) starts a new tap sequence.
const TAP_WINDOW_MS = 2000
const TAP_MAX = 8

const clampBpm = v => Math.min(MAX_BPM, Math.max(MIN_BPM, v))
const clampOffset = v => Math.min(MAX_OFFSET_MS, Math.max(-MAX_OFFSET_MS, Math.round(v)))

export function createTempo({ now = () => performance.now(), playing = false } = {}) {
	let bpm = DEFAULT_BPM
	let beat0 = 0
	let sec0 = 0
	let t0 = now()
	// Ms the drives' beat runs ahead of the music's, so a delayed output lands on the beat.
	let offset = 0
	let taps = []
	let snap = null
	const subs = new Set()
	const tapSubs = new Set()

	// The anchor, downbeat and taps are the music's beat, as heard.
	const musicBeatAt = (t = now()) => (playing ? beat0 + ((t - t0) * bpm) / 60000 : beat0)
	const beatAt = (t = now()) => musicBeatAt(t + offset)
	const secondsAt = (t = now()) => (playing ? sec0 + (t - t0) / 1000 : sec0)
	const reanchor = t => {
		beat0 = musicBeatAt(t)
		sec0 = secondsAt(t)
		t0 = t
	}
	const notify = () => {
		snap = null
		for (const fn of subs) fn()
	}

	return {
		/** The beat the drives play: the music's, `offset` ms ahead. */
		beatAt,
		musicBeatAt,
		/** Transport seconds: stand still while paused, unaffected by the BPM. */
		secondsAt,
		/** Seconds per beat at the current tempo. */
		beatSeconds: () => 60 / bpm,
		snapshot: () => (snap ??= { bpm, playing, offset, source: 'internal' }),
		/** The whole clock at `t`, for another client to `adopt`. */
		state: (t = now()) => ({ bpm, playing, offset, beat: musicBeatAt(t), sec: secondsAt(t) }),
		/** Take another client's clock: `state` as it read at local time `t`. */
		adopt(state, t = now()) {
			const { bpm: b, playing: p, offset: o, beat, sec } = state ?? {}
			if (![b, beat, sec].every(Number.isFinite)) return
			bpm = clampBpm(b)
			playing = p !== false
			if (Number.isFinite(o)) offset = clampOffset(o)
			beat0 = beat
			sec0 = sec
			t0 = t
			notify()
		},
		/** Every tap, from any input, whether or not it changed the BPM. */
		onTap(fn) {
			tapSubs.add(fn)
			return () => tapSubs.delete(fn)
		},
		setOffset(v) {
			const next = clampOffset(v)
			if (!Number.isFinite(next) || next === offset) return
			offset = next
			notify()
		},
		subscribe(fn) {
			subs.add(fn)
			return () => subs.delete(fn)
		},
		setBpm(v, t = now()) {
			const next = clampBpm(v)
			if (!Number.isFinite(next) || next === bpm) return
			reanchor(t)
			bpm = next
			notify()
		},
		/** Two or more taps, each within the window of the last, set the BPM from the mean interval of the latest eight and land the last tap on a whole beat. */
		tap(t = now()) {
			for (const fn of tapSubs) fn()
			taps = t - taps.at(-1) > TAP_WINDOW_MS ? [] : taps.slice(-(TAP_MAX - 1))
			taps.push(t)
			if (taps.length < 2) return
			const mean = (taps.at(-1) - taps[0]) / (taps.length - 1)
			if (!(mean > 0)) return
			reanchor(t)
			bpm = clampBpm(Math.round((60000 / mean) * 10) / 10)
			beat0 = Math.round(beat0)
			notify()
		},
		/** Now is the first beat of a bar: the nearest bar line, so a long loop keeps its place. */
		downbeat(t = now()) {
			reanchor(t)
			beat0 = Math.round(beat0 / BEATS_PER_BAR) * BEATS_PER_BAR
			notify()
		},
		play(t = now()) {
			if (playing) return
			t0 = t
			playing = true
			notify()
		},
		pause(t = now()) {
			if (!playing) return
			reanchor(t)
			playing = false
			notify()
		},
		/** Pause and rewind to the first beat. */
		stop(t = now()) {
			playing = false
			beat0 = 0
			sec0 = 0
			t0 = t
			notify()
		},
	}
}

const tempo = createTempo()
export default tempo
